import Progress from 'easy-circular-progress';
import { Inject } from 'vue-di-container';
import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import LoaderService from '../../../Services/LoaderService';
import { Toaster } from '../../../Services/toast';
import { SenderType } from '../../../shared/Enums/SenderType';
import UserRoleService from '../../../shared/userService/UserRoleService';
import UserService from '../../../shared/userService/UserService';
import { canProcessComplaints as resolveCanProcessComplaints, parseAllowedScreensFromPermissionsResponse } from '../../../shared/screenPermissionHelper';
import PermissionManagementService from '../../UserManagement/PermissionManagement/PermissionManagementService';
import SenderComplaintsService from '../SenderComplaintsService';
import WithRender from './ComplaintDetails.html';
import { ComplaintDetailsModel } from './ComplaintDetailsModel';
import { ComplaintGroupedRowModel } from './ComplaintGroupedRowModel';
import { formatFraudTableSenderName, sortLinkedComplaintsOldestFirst } from './complaintGroupedHelpers';
import FileSaver from 'file-saver';
import { ComplaintAttachmentModel } from './ComplaintAttachmentModel';
import { EditComplaintDetailsModel } from './EditComplaintDetailsModel';


@WithRender
@Component({
    components: {
        Progress,
    },
    diProvide: [
        UserService,
        UserRoleService,
        SenderComplaintsService,
        PermissionManagementService,
    ],
})
export default class ComplaintDetails extends Vue {
    @Inject(UserService) public userService!: UserService;
    @Inject(UserRoleService) public userRoleService!: UserRoleService;
    @Inject(LoaderService) public loaderService!: LoaderService;
    @Inject(SenderComplaintsService) public senderComplaintsService!: SenderComplaintsService;
    @Inject(PermissionManagementService) public permissionManagementService!: PermissionManagementService;
    @Prop({ type: Boolean, default: false })
    @Prop({ type: String, default: '' })
    public layoutSup: boolean | any;

    public complaint: ComplaintDetailsModel = {
        complaintId: '',
        senderType: 0,
        clientName: '',
        referenceNumber: '',
        providerName: '',
        customerType: 0,
        representativeEmail: '',
        representativePhone: '',
        representativeName: '',
        senderName: '',
        isCritical: false,
        senderExpiryDate: '',
        deactivatedBy: '',
        reactivatedBy: '',
        gracePeriod: 0,
        senderStatus: 0,
        complaintStatus: 0,
        attachmentList: [],
        comments: '',
        linkedComplaints: [],
        complaintDate: '',
        isBlacklist: false,
    };
    public reactivateids: string[] = [];
    public editComplaintModel: EditComplaintDetailsModel = {
        comments: '',
        attachmentList: [],
        tcn: ''
    };
    public reactivateDialog: boolean = false;
    public closeComplaintDialog: boolean = false;
    public deactivateComplaintDialog: boolean = false;
    public editComplaintDialog: boolean = false;
    public typeFile: string = '';
    public headers: any = [
        { text: this.$t('shared.attachmentName'), value: 'fileName', align: 'center', sortable: false },
        { text: this.$t('shared.addedDate'), value: 'createdDate', align: 'center', sortable: false },
        { text: this.$t('shared.actions'), sortable: false, value: 'id', align: 'center', width: '100' },
    ];
    public attachmentName: string = '';
    public userRole: number = 0;
    public canProcessComplaints: boolean = false;
    public allowedScreens: Set<string> | null = null;
    public User: any;
    public customerType: string = '';
    public id: string | any;
    public isEditMode: boolean = false;
    public editText: string = this.$t('shared.enableToEdit').toString();
    public senderType: SenderType[] = [];
    public async mounted() {
        this.$emit('childinit', this.layoutSup);
        this.User = await this.userService.getUser();
        this.userRole = this.userRoleService.GetUserRole(this.User);
        await this.loadProcessingPermission();
        if (this.canProcessComplaints) {
            this.getHeaders();
        }
        this.id = this.$route.params.id;
        const response = await this.senderComplaintsService.GetDetails(this.id);
        if (response === false) {
            return;
        }
        if (response.isSuccess === false) {
            Toaster.error(response.errorMessage || '');
            return;
        }
        if (response.result) {
            this.complaint = this.mergeComplaintFromApi(response.result);
            if (response.result.complaintId != null && String(response.result.complaintId) !== '') {
                this.id = String(response.result.complaintId);
            }
            this.getSenderType();
        }
    }
    private async loadProcessingPermission(): Promise<void> {
        if (this.userRole === 4) {
            const res = await this.permissionManagementService.GetPermissions(this.User.userId);
            this.allowedScreens = parseAllowedScreensFromPermissionsResponse(res);
        }
        this.canProcessComplaints = resolveCanProcessComplaints(this.userRole, this.allowedScreens);
    }

    public get displayedLinkedComplaints(): ComplaintGroupedRowModel[] {
        return sortLinkedComplaintsOldestFirst(this.complaint.linkedComplaints || []);
    }
    public get fraudLinkedComplaintsHeaders(): any[] {
        if (!this.canProcessComplaints) {
            return [];
        }
        return [
            { text: this.$t('senderComplaints.tcnNumber').toString(), value: 'tcn', align: 'center', sortable: false },
            { text: this.$t('senderComplaints.groupedTableSenderName').toString(), value: 'senderName', align: 'center', sortable: false },
            { text: this.$t('senderComplaints.complaintDate').toString(), value: 'complaintDate', align: 'center', sortable: false },
        ];
    }
    public get fraudSenderGroupingTableRows(): { tcn: string; senderName: string; complaintDate: string }[] {
        if (!this.canProcessComplaints) {
            return [];
        }
        const primaryRawDate = this.complaint.complaintDate != null ? this.complaint.complaintDate : '';
        const primaryTcn = this.complaint.complaintId != null && String(this.complaint.complaintId) !== ''
            ? String(this.complaint.complaintId)
            : (this.id != null && this.id !== '' ? String(this.id) : '');
        const primaryIsBlack = !!this.complaint.isBlacklist;
        const primary = {
            tcn: primaryTcn,
            senderName: formatFraudTableSenderName(this.complaint.senderName, primaryIsBlack),
            complaintDate: this.formatFraudTableDate(primaryRawDate),
            _sortTime: this.parseComplaintDateForSort(primaryRawDate),
        };
        const linked = this.displayedLinkedComplaints.map((row) => ({
            tcn: row.transactionCommunicationNumber,
            senderName: formatFraudTableSenderName(row.senderName, !!row.isBlacklist),
            complaintDate: this.formatFraudTableDate(row.openedAt),
            _sortTime: this.parseComplaintDateForSort(row.openedAt),
        }));
        const combined = [primary, ...linked];
        combined.sort((a, b) => a._sortTime - b._sortTime);
        return combined.map(({ _sortTime, ...row }) => row);
    }
    private parseComplaintDateForSort(raw: string): number {
        if (!raw || !String(raw).trim()) {
            return Number.MAX_SAFE_INTEGER;
        }
        const t = Date.parse(raw);
        return Number.isNaN(t) ? Number.MAX_SAFE_INTEGER : t;
    }
    private formatFraudTableDate(raw: string): string {
        if (!raw || !String(raw).trim()) {
            return '-';
        }
        const t = Date.parse(raw);
        if (Number.isNaN(t)) {
            return String(raw).trim();
        }
        return new Date(t).toLocaleDateString('en-GB');
    }
    public formatSenderTypeMask(mask: number): string {
        const parts: string[] = [];
        if ((mask & SenderType.Advertisement) === SenderType.Advertisement) {
            parts.push(this.getSenderTypeText(SenderType.Advertisement));
        }
        if ((mask & SenderType.Service) === SenderType.Service) {
            parts.push(this.getSenderTypeText(SenderType.Service));
        }
        if ((mask & SenderType.Awareness) === SenderType.Awareness) {
            parts.push(this.getSenderTypeText(SenderType.Awareness));
        }
        if ((mask & SenderType.Warning) === SenderType.Warning) {
            parts.push(this.getSenderTypeText(SenderType.Warning));
        }
        return parts.length ? parts.join(', ') : '-';
    }
    private readIsBlacklistFromPayload(obj: any): boolean {
        const keys = ['isBlacklist', 'IsBlacklist', 'IsBlacklist', 'IsBlacklist'];
        for (let i = 0; i < keys.length; i++) {
            const v = obj[keys[i]];
            if (typeof v === 'boolean') {
                return v;
            }
        }
        return false;
    }
    private mergeComplaintFromApi(result: ComplaintDetailsModel | Record<string, unknown>): ComplaintDetailsModel {
        const r = result as any;
        const rawLinks = r.linkedComplaints != null ? r.linkedComplaints : r.LinkedComplaints;
        const links: any[] = Array.isArray(rawLinks) ? rawLinks : [];
        const normalizedLinks = links.map((row: any) => ({
            complaintId: String(row.complaintId || row.ComplaintId || ''),
            transactionCommunicationNumber: String(row.transactionCommunicationNumber || row.TransactionCommunicationNumber || ''),
            senderName: String(row.senderName || row.SenderName || ''),
            senderType: Number(row.senderType != null ? row.senderType : (row.SenderType != null ? row.SenderType : 0)),
            openedAt: String(row.openedAt || row.OpenedAt || ''),
            isBlacklist: this.readIsBlacklistFromPayload(row),
        }));
        const isBlack = this.readIsBlacklistFromPayload(r);
        return {
            ...(result as ComplaintDetailsModel),
            isBlacklist: isBlack,
            linkedComplaints: normalizedLinks,
        };
    }
    public getHeaders() {
        const fruadUserColumn =
            { text: this.$t('shared.addedBy'), value: 'createdBy', align: 'center', sortable: false };
        this.headers.splice(2, 0, fruadUserColumn);
    }
    public convertEdit() {
        this.isEditMode = !this.isEditMode;
        if (this.isEditMode) {
            this.editText = this.$t('shared.disableToEdit').toString();
        } else {
            this.editText = this.$t('shared.enableToEdit').toString();
        }
    }
    public onPickFile() {
        (this.$refs.contractAttachment as any).click();
    }
    public showEditComplaintDialog(id: string) {
        this.editComplaintDialog = true;
    }
    public hideEditComplaintDialog() {
        this.editComplaintDialog = false;
    }
    public getRate() {
        return (this.complaint.gracePeriod / 30) * 100;
    }
    public async save() {
        const rootTcn = this.complaint.complaintId != null && String(this.complaint.complaintId) !== ''
            ? String(this.complaint.complaintId)
            : String(this.id);
        this.editComplaintModel.tcn = rootTcn;
        this.editComplaintModel.comments = this.complaint.comments;
        const response = await this.senderComplaintsService.EditComplaint(this.editComplaintModel);
        if (!response.isSuccess) {
            Toaster.error(response.errorMessage);
        } else {
            Toaster.success(this.$t('senderComplaints.complaintEditedSuccessfully').toString());
        }
        this.editComplaintDialog = false;
        await this.reload();
    }
    public get canAddAttachment(): boolean {
        return this.complaint.attachmentList.length < 3
    }

    public get hasMenuOptions() {
        return (
            (this.complaint.complaintStatus === 2 && this.complaint.senderStatus === 4
                && this.canProcessComplaints) ||
            (this.complaint.complaintStatus === 1 && this.canProcessComplaints &&
                (this.complaint.senderStatus === 7 || this.complaint.senderStatus === 5))
        );
    }

    public onFilePicked(event: any) {
        const files: any[] = event.target.files;
        const modelArray: ComplaintAttachmentModel = {} as ComplaintAttachmentModel;
        const editArray: ComplaintAttachmentModel = {} as ComplaintAttachmentModel;
        Array.prototype.forEach.call(files, (file, index) => {
            modelArray.fileName = file.name;
            if (file.size > 3000000) {
                Toaster.error(this.$t('shared.fileSizeExceedsTheLimit').toString());
            }
            else {
                this.attachmentName = file.name;
                const reader = new FileReader();
                reader.onloadend = (fileData: any) => {
                    modelArray.fileContent = fileData.target.result.split(',')[1];
                    modelArray.createdBy = this.User.username;
                    modelArray.createdDate = new Date().toLocaleDateString("en-GB").replace(/\//g, "-");
                    this.complaint.attachmentList.push(modelArray);
                    editArray.createdDate = new Date().toISOString();
                    editArray.fileName = file.name;
                    editArray.fileContent = fileData.target.result.split(',')[1];
                    this.editComplaintModel.attachmentList.push(editArray);
                };
                reader.readAsDataURL(file);
            }
        });
    }
    public checkColor(typeStatus: string) {
        typeStatus = typeStatus.toString();
        switch (typeStatus) {
            case '1': {
                return '#c427ba';
            }
            case '2': {
                return '#5aaa0b';
            }
            case '3': {
                return '#C42727';
            }
            case '4': {
                return '#aaa7a7';
            }
            case '5': {
                return '#014bb1';
            }
            case '7': {
                return '#5c9bf2';
            }
            default: {
                return '#F78500';
            }
        }
    }
   
    public GetSenderStatus(typeStatus: number) {
        switch (typeStatus) {
            case 1: {
                return 'Pending Approval';
            }
            case 2: {
                return 'Approved';
            }
            case 3: {
                return 'Rejected';
            }
            case 4: {
                return 'Deactivated';
            }
            case 5: {
                return 'Expired';
            }
            case 6: {
                return 'Deleted';
            }
            case 7: {
                return 'Paid';
            }
            case 9: {
                return 'Pending For Verify';
            }
            case 10: {
                return 'Pending For Review';
            }
        }
    }
    public getCustomerTypeText(typeId: number) {
        switch (typeId) {
            case 1:
                return this.$t('shared.government').toString();
            case 2:
                return this.$t('shared.charity').toString();
            case 3:
                return this.$t('shared.private').toString();
            case 4:
                return this.$t('shared.international').toString();
            default:
                return '-';
        }
    }

    public getSenderType() {
        const senderBinary = this.complaint.senderType;
        if ((senderBinary & 1) === 1) {
            this.senderType.push(1);
        }
        if ((senderBinary & 2) === 2) {
            this.senderType.push(2);
        }
        if ((senderBinary & 4) === 4) {
            this.senderType.push(4);
        }
        if ((senderBinary & 8) === 8) {
            this.senderType.push(8);
        }
    }
    public showReactivateDialog(id: string) {
        if (id !== '') {
            this.reactivateids.push(id);
        }
        this.reactivateDialog = true;
    }
    public showCloseComplaintDialog() {
        this.closeComplaintDialog = true;
    }
    public hideReactivateDialog() {
        this.reactivateDialog = false;
    }
    public showDeactivateComplaintDialog() {
        this.deactivateComplaintDialog = true;
    }
    public hideDeactivateComplaintDialog() {
        this.deactivateComplaintDialog = false;
    }
    public hideCloseComplaintDialog() {
        this.closeComplaintDialog = false;
    }
    public async Reactivate() {
        let ids: string[] = [];
        ids.push(this.id);
        const response = await this.senderComplaintsService.Reactivate(ids);
        if (!response.isSuccess) {
            Toaster.error(response.errorMessage);
            this.reactivateDialog = false;
            await this.reload();
        } else {
            this.reactivateDialog = false;
            Toaster.success(this.$t('senderComplaints.reactivatedsuccessfully').toString());
            await this.reload();
        }
    }

    public async closeComplaint() {
        const response = await this.senderComplaintsService.closeComplaint(this.id);
        if (!response.isSuccess) {
            Toaster.error(response.errorMessage);
        } else {
            Toaster.success(this.$t('senderComplaints.complaintClosedSuccessfully').toString());
        }
        await this.reload();
        this.closeComplaintDialog = false;
    }
    public async deactivateComplaint() {
        const response = await this.senderComplaintsService.deactivateComplaint(this.id);
        if (!response.isSuccess) {
            Toaster.error(response.errorMessage);
        } else {
            Toaster.success(this.$t('senderComplaints.complaintDeactivatedsuccessfully').toString());
        }
        await this.reload();
        this.deactivateComplaintDialog = false;
    }
    public async reload() {
        this.id = this.$route.params.id;
        const response = await this.senderComplaintsService.GetDetails(this.id);
        if (response !== false) {
            if (response.isSuccess === false) {
                Toaster.error(response.errorMessage || '');
            } else if (response.result) {
                this.complaint = this.mergeComplaintFromApi(response.result);
                if (response.result.complaintId != null && String(response.result.complaintId) !== '') {
                    this.id = String(response.result.complaintId);
                }
                this.getSenderType();
            }
        }
        this.editComplaintModel = {
            comments: '',
            attachmentList: [],
            tcn: ''
        };
        this.isEditMode = false;
    }
    public getSenderTypeText(typeId: number) {
        switch (typeId) {
            case SenderType.Advertisement:
                return this.$t('shared.advertisement').toString();
            case SenderType.Service:
                return this.$t('shared.service').toString();
            case SenderType.Awareness:
                return this.$t('shared.awareness').toString();
            case SenderType.Warning:
                return this.$t('shared.warning').toString();
            default:
                return '-';
        }
    }
    public async downloadFile(file: ComplaintAttachmentModel) {
        const fileContent = await this.senderComplaintsService.GetFileContent(file.id);
        const array = Buffer.from(fileContent, 'base64');
        const blob = new Blob([array], { type: 'application/pdf' });
        FileSaver.saveAs(blob, file.fileName);
    }
}